import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { PersonId } from '@shared/types.ts';
import { Icon } from './Icon.tsx';
import { cameBack, Link, usePath } from '../lib/router.tsx';
import { getStore, useAppState, useFeature, useUi } from '../data/store.ts';

/**
 * Renders `children` right after the screen's first paint, so a tab switches the moment it's
 * tapped even when its content takes a moment to build. A slide (view transition) renders at
 * once instead, since its snapshot needs the content. The fallback (a shimmer) fades in only
 * if the wait is long enough to notice.
 */
export function AfterFirstPaint({ fallback, children }: { fallback: ReactNode; children: ReactNode }) {
  // A slide, or Back (which puts the scroll position back), needs the content at once.
  const [ready, setReady] = useState(() => cameBack() || (typeof document !== 'undefined' && !!document.documentElement.dataset.nav));
  useEffect(() => {
    if (ready) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const frame = requestAnimationFrame(() => {
      timer = setTimeout(() => setReady(true), 0);
    });
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [ready]);
  return ready ? <>{children}</> : <div className="after-paint-wait">{fallback}</div>;
}

export function Avatar({ person, small }: { person: PersonId | undefined; small?: boolean }) {
  const people = useAppState((s) => s.people);
  const p = people.find((x) => x.id === person);
  if (!p) return null;
  return (
    <span className={`avatar ${p.id}${small ? ' sm' : ''}`} title={`Added by ${p.name}`} aria-label={`Added by ${p.name}`}>
      {p.initial}
    </span>
  );
}

/** Perishable or pantry: decides which carts an item can go to. `guessed` labels an unconfirmed guess. */
export function KindToggle({
  perishable,
  guessed,
  goesTo,
  onChange,
}: {
  perishable: boolean;
  guessed: boolean;
  /** Names of the stores this kind can go to. */
  goesTo: string[];
  onChange: (perishable: boolean) => void;
}) {
  return (
    <div className="field">
      <span style={{ fontSize: 13, fontWeight: 600 }}>Kind{guessed ? ' (our guess)' : ''}</span>
      <div className="row" role="radiogroup" aria-label="Kind" style={{ gap: 8 }}>
        {[
          { value: true, label: 'Perishable' },
          { value: false, label: 'Pantry or household' },
        ].map((o) => (
          <button
            key={o.label}
            type="button"
            role="radio"
            aria-checked={perishable === o.value}
            className={`chip${perishable === o.value ? ' chip-gold' : ''}`}
            style={{ minHeight: 44 }}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
      <span className="help">{goesTo.length ? `Can go to ${goesTo.join(' or ')}.` : 'No store takes this kind yet. Check Settings, Stores.'}</span>
    </div>
  );
}

export function Stepper({
  value,
  onChange,
  label,
  boxed,
  min = 0,
}: {
  value: number;
  onChange: (n: number) => void;
  label: string;
  boxed?: boolean;
  min?: number;
}) {
  return (
    <div role="group" aria-label={label} className={`stepper${boxed ? ' boxed' : ''}`}>
      <button type="button" aria-label={`Decrease ${label}`} onClick={() => onChange(Math.max(min, value - 1))}>
        −
      </button>
      <span className="qty" aria-live="polite">
        {value}
      </span>
      <button type="button" aria-label={`Increase ${label}`} onClick={() => onChange(value + 1)}>
        +
      </button>
    </div>
  );
}

export function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prev?.focus?.();
    };
  }, [onClose]);
  // On the page itself, so a sheet opened from a layer still covers the tab bar.
  return createPortal(
    <>
      <div className="sheet-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <span className="sheet-handle" aria-hidden="true" />
        {children}
      </div>
    </>,
    document.body,
  );
}

export function SheetHeader({ title, subtitle, onClose, extra }: { title: string; subtitle?: string; onClose: () => void; extra?: ReactNode }) {
  return (
    <div className="row-between">
      <div className="stack" style={{ gap: 2 }}>
        <h1 className="h1 h1-sm" style={{ fontSize: 26 }}>
          {title}
        </h1>
        {subtitle && <span className="small muted">{subtitle}</span>}
      </div>
      <div className="row" style={{ gap: 8 }}>
        {extra}
        <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}>
          <Icon name="close" size={18} />
        </button>
      </div>
    </div>
  );
}

export function TabBar() {
  const path = usePath();
  const attention = useAppState((s) => s.emails);
  const needsLook = attention.some((e) => e.status === 'failed');
  const groceries = useFeature('groceries');
  const money = useFeature('money');
  const language = useFeature('language');
  const tabs = [
    ...(groceries ? [{ to: '/groceries', label: 'Groceries', icon: 'bag' as const }] : []),
    { to: '/trips', label: 'Travel', icon: 'plane' as const, dot: needsLook },
    ...(money ? [{ to: '/money', label: 'Money', icon: 'wallet' as const }] : []),
    ...(language ? [{ to: '/language', label: 'Language', icon: 'cards' as const }] : []),
    { to: '/settings', label: 'Settings', icon: 'gear' as const },
  ];
  return (
    <nav className="tabbar" aria-label="Main">
      {tabs.map((t) => (
        <Link key={t.to} to={t.to} transition="none" aria-current={path.startsWith(t.to) ? 'page' : undefined}>
          <Icon name={t.icon} size={24} />
          {t.label}
          {t.dot && <span className="tab-dot" aria-label="Needs attention" />}
        </Link>
      ))}
    </nav>
  );
}

export function Toasts() {
  const { toasts } = useUi();
  const store = getStore();
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((t) => (
        <div className="toast" key={t.id} role="status">
          <span className="grow">{t.text}</span>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action!();
                store.dismissToast(t.id);
              }}
            >
              {t.actionLabel ?? 'Undo'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

export function ScreenTitle({ title, eyebrow = 'Natangelo', right }: { title: string; eyebrow?: string; right?: ReactNode }) {
  return (
    <div className="screen-header">
      <div className="stack" style={{ gap: 2 }}>
        <span className="eyebrow">{eyebrow}</span>
        <h1 className="h1">{title}</h1>
      </div>
      {right}
    </div>
  );
}

export function money(n: number | undefined, digits = 2): string {
  if (n == null) return '—';
  return `$${n.toFixed(digits)}`;
}
